import {
  Box,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  detailsTickets,
  setDetailsData,
  tripData,
} from 'src/redux/Calendar/sliceCalendar';
import ForwardIcon from '@material-ui/icons/Forward';
import {
  IScheduleDetails,
  ISchedulesDetailsData,
} from 'src/redux/Calendar/interfaces';
import { normalizeTime } from 'src/helpers/calendar/normalizeTime';
import { useStyles } from './styles';

interface IScheduleMaster
  extends Omit<IScheduleDetails, 'calendar_id' | 'sold'> {}

export const SchedulesMasterUpdateForm = ({
  schedule_id,
}: {
  schedule_id?: number;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const data = useSelector(tripData);
  const detailedData = useSelector(detailsTickets);
  const currentSchedule = data?.schedules.find((i) => i.id === schedule_id);

  const [state, setState] = useState<IScheduleMaster>({
    min_tickets: null,
    max_tickets: null,
    is_disabled: '0',
  });

  if (schedule_id && !currentSchedule) return null;

  const changeState = (data: Partial<IScheduleMaster>) => {
    setState((prev) => ({ ...prev, ...data }));
  };

  const applyChanges = (name: keyof IScheduleMaster) => {
    if (!detailedData) return;
    dispatch(
      setDetailsData(
        Object.keys(detailedData).reduce((acc: ISchedulesDetailsData, curr) => {
          return {
            ...acc,
            [curr]: Object.keys(detailedData[curr]).reduce(
              (
                accD: {
                  [date: string]: IScheduleDetails;
                },
                currD
              ) => {
                return {
                  ...accD,
                  [currD]: {
                    ...detailedData[curr][currD],
                    ...((!schedule_id || schedule_id === +curr) && {
                      [name]: state[name],
                    }),
                  },
                };
              },
              {}
            ),
          };
        }, {})
      )
    );
  };

  return (
    <Box
      bgcolor={schedule_id ? '#e6fcfa' : '#c0f3ef'}
      borderBottom={'0.8px solid rgba(0, 0, 0, 0.12)'}
      borderTop={!!schedule_id && '0.8px solid rgba(0, 0, 0, 0.12)'}
      px={3}
      position={!!schedule_id && 'sticky'}
      mt={!!schedule_id && 1}
      zIndex={10}
      top={0}>
      <Box py={0.5} display="flex" alignItems="center">
        {schedule_id ? (
          <Typography variant="body1">
            Bulk update schedule at{' '}
            <strong>
              {currentSchedule
                ? normalizeTime(currentSchedule.hour, currentSchedule.minute)
                : '-'}
            </strong>
          </Typography>
        ) : (
          <Typography variant="button" style={{ fontWeight: 500 }}>
            Bulk Update for All Schedules
          </Typography>
        )}
      </Box>
      <Divider variant="fullWidth" />
      <Box
        display="flex"
        pb={2}
        pt={3}
        justifyContent="center"
        flexWrap="wrap"
        gridGap="10px">
        <Box mr={2} display="flex" alignItems="center">
          <TextField
            type="number"
            placeholder={currentSchedule?.min_tickets?.toString() || ''}
            value={state.min_tickets || ''}
            onChange={(e) => changeState({ min_tickets: e.target.value })}
            variant="outlined"
            className={classes.inputWrapper}
            InputProps={{
              style: {
                width: '140px',
                height: '40px',
                background: '#fff',
              },
            }}
            inputProps={{
              min: 0,
              step: 1,
            }}
            size="small"
            InputLabelProps={{
              shrink: true,
              style: {
                letterSpacing: '0.04em',
              },
            }}
            label="Min Tickets"
          />
          <Box
            style={{ cursor: 'pointer' }}
            display="flex"
            ml={0.5}
            onClick={() => applyChanges('min_tickets')}>
            <ForwardIcon style={{ rotate: '90deg' }} />
          </Box>
        </Box>
        <Box mr={2} display="flex" alignItems="center">
          <TextField
            type="number"
            placeholder={currentSchedule?.max_tickets?.toString() || ''}
            value={state.max_tickets || ''}
            onChange={(e) => changeState({ max_tickets: e.target.value })}
            variant="outlined"
            className={classes.inputWrapper}
            InputProps={{
              style: {
                width: '140px',
                height: '40px',
                background: '#fff',
              },
            }}
            inputProps={{
              min: 0,
              step: 1,
            }}
            size="small"
            InputLabelProps={{
              shrink: true,
              style: {
                letterSpacing: '0.04em',
              },
            }}
            label="Max Tickets"
          />
          <Box
            style={{ cursor: 'pointer' }}
            display="flex"
            ml={0.5}
            onClick={() => applyChanges('max_tickets')}>
            <ForwardIcon style={{ rotate: '90deg' }} />
          </Box>
        </Box>
        <Box display="flex">
          <Box display="flex" alignItems="center" mr={4} ml={6}>
            <FormControlLabel
              control={<Switch className={classes.customSwitch} />}
              label="Disable"
              checked={Boolean(state.is_disabled && +state.is_disabled)}
              onChange={(_, checked) =>
                changeState({ is_disabled: checked ? '1' : '0' })
              }
            />
            <Box
              style={{ cursor: 'pointer' }}
              display="flex"
              onClick={() => applyChanges('is_disabled')}>
              <ForwardIcon style={{ rotate: '90deg' }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
